/**
 * React Query mutation hook for copying an RFP via bp-api.
 *
 * POST /bp/v1/rfps/:id/copy
 */

import { AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@platform/api";

export interface CopyRfpResponse {
  rfpId: number;
}

async function copyRfp(
  axios: AxiosInstance,
  rfpUuid: string,
): Promise<CopyRfpResponse> {
  const { data } = await axios.post<CopyRfpResponse>(
    `/bp/v1/rfps/${encodeURIComponent(rfpUuid)}/copy`,
  );

  return data;
}

/**
 * Hook that returns a mutation for copying an RFP.
 * Invalidates the RFP list queries on success so the new copy appears.
 */
export function useCopyRfp(): ReturnType<
  typeof useMutation<CopyRfpResponse, Error, string>
> {
  const axios = useApi();
  const queryClient = useQueryClient();

  return useMutation<CopyRfpResponse, Error, string>({
    mutationFn: (rfpUuid: string) => copyRfp(axios, rfpUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfps"] });
    },
  });
}
